import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Trash2, ChevronDown, ChevronUp, RotateCcw, Loader2 } from 'lucide-react';
import { supplementsApi } from '../api';
import { useLanguage } from '../contexts/LanguageContext';

function formatDate(iso, language) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const locale = language === 'pt' ? 'pt-PT' : 'en-GB';
    return d.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
}

/**
 * Collapsed "Recently deleted" section for supplement plans.
 * Mirrors RecentlyDeletedDietPlans; hides itself entirely when the Trash is empty.
 *
 * Props:
 *  - onRestored: () => void  — parent refetches its plan list
 */
export default function RecentlyDeletedSupplementPlans({ onRestored }) {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const [expanded, setExpanded] = useState(false);

  const query = useQuery({
    queryKey: ['supplement-plans-deleted'],
    queryFn: async () => (await supplementsApi.listDeletedPlans()).data,
  });

  const restore = useMutation({
    mutationFn: (id) => supplementsApi.restoreDeletedPlan(id),
    onSuccess: () => {
      toast.success(t('supplementPlan.history.restored', 'Supplement plan restored'));
      query.refetch();
      onRestored?.();
    },
    onError: (err) => {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === 'string' ? msg : t('common.error', 'Error'));
    },
  });

  const items = query.data || [];
  if (!query.isLoading && items.length === 0) {
    return null;
  }

  return (
    <div className="rounded-xl border border-border/40 mt-4" data-testid="deleted-supplement-plans-section">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-muted/30 transition-colors rounded-xl"
        aria-expanded={expanded}
      >
        <Trash2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span className="text-sm font-semibold flex-1 text-left">
          {t('supplementPlan.history.recentlyDeleted', 'Recently deleted')}
        </span>
        <span className="text-xs text-muted-foreground">{items.length}</span>
        {expanded
          ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
          : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>
      {expanded && (
        <ul className="border-t border-border/30 divide-y divide-border/20">
          {items.map((it) => {
            const busy = restore.isPending && restore.variables === it.id;
            return (
              <li
                key={it.id}
                className="flex items-center gap-3 px-4 py-3"
                data-testid={`deleted-supplement-plan-${it.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{it.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {it.supplements_count} {t('supplementPlan.history.supplements', 'supplements')}
                    {it.deleted_at
                      ? ` · ${t('supplementPlan.history.deletedOn', 'deleted on')} ${formatDate(it.deleted_at, language)}`
                      : ''}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => restore.mutate(it.id)}
                  data-testid={`restore-deleted-supplement-plan-${it.id}`}
                >
                  {busy
                    ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                    : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
                  {t('supplementPlan.history.restoreDeleted', 'Restore')}
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
